import React from 'react';
import { Card, Empty, Button, Alert, Tag, Descriptions, Typography } from 'antd';
import { RadarChartOutlined } from '@ant-design/icons';
import { useChainCockpit } from '../hooks/useChainCockpit';
import ChainListSidebar from './ChainCockpit/ChainListSidebar';
import CockpitControlCard from './ChainCockpit/CockpitControlCard';
import TraceGroupCard from './ChainCockpit/TraceGroupCard';
import SideEffectsCard from './ChainCockpit/SideEffectsCard';
import {
  chainStatusColorMap,
  evidenceColorMap,
  domainColorMap,
} from './ChainCockpit/helpers';

const { Text } = Typography;

interface Props {
  /** 切換到「資料流觀測」Tab（由 index.tsx 傳入） */
  onGoFlowObserve: () => void;
}

const ChainCockpitPanel: React.FC<Props> = ({ onGoFlowObserve }) => {
  const {
    chains,
    chainsLoading,
    selectedChainId,
    selectChain,
    config,
    configLoading,
    configError,
    docNo,
    setDocNo,
    trace,
    traceLoading,
    runTrace,
    autoRefresh,
    setAutoRefresh,
    lastRefreshedAt,
  } = useChainCockpit();

  // hasTrace=false 時用 getChains 既有資料補 chain 基本資訊
  const selectedChainItem = chains.find((c) => c.chainId === selectedChainId) || null;

  const renderNoTrace = () => (
    <>
      {selectedChainItem && (
        <Card size="small" className="mb-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="font-bold" style={{ fontSize: 16 }}>
              {selectedChainItem.chainName}
            </span>
            <span className="font-mono text-gray-400" style={{ fontSize: 12 }}>
              {selectedChainItem.chainId}
            </span>
            <Tag color={chainStatusColorMap[selectedChainItem.currentStatus] || 'default'}>
              {selectedChainItem.currentStatus.replace(/_/g, ' ')}
            </Tag>
            <Tag color={evidenceColorMap[selectedChainItem.evidenceLevel] || 'default'}>
              {selectedChainItem.evidenceLevel.replace(/_/g, ' ')}
            </Tag>
          </div>
          <Descriptions size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label="領域">
              <Tag color={domainColorMap[selectedChainItem.businessDomain] || 'default'}>
                {selectedChainItem.businessDomain}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="起始表單">
              <span className="font-mono">{selectedChainItem.startFormId}</span>
            </Descriptions.Item>
            <Descriptions.Item label="節點數">
              {selectedChainItem.readyCount}/{selectedChainItem.nodeCount} ready
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
      <Card>
        <Empty description="此鏈尚未設定追蹤映射" />
      </Card>
    </>
  );

  const renderGuide = () => (
    <Card>
      <Empty
        description={
          <div style={{ maxWidth: 480, margin: '0 auto' }}>
            <Text type="secondary">
              輸入測試單號後，這裡會顯示該單據流過整條鏈的足跡。
              建議搭配「資料流觀測」Tab 同時使用：先開觀察窗，再操作表單，兩邊互相印證。
            </Text>
          </div>
        }
      >
        <Button type="link" icon={<RadarChartOutlined />} onClick={onGoFlowObserve}>
          前往資料流觀測
        </Button>
      </Empty>
    </Card>
  );

  const renderTraceResult = () => {
    if (!trace) return null;
    return (
      <>
        {trace.groups.map((g) => (
          <div key={g.formId} className="mb-6">
            <TraceGroupCard group={g} />
          </div>
        ))}
        <div className="mb-6">
          <SideEffectsCard sideEffects={trace.sideEffects || []} />
        </div>
      </>
    );
  };

  const renderCockpit = () => {
    if (configLoading) {
      return <Card loading title="鏈駕駛艙" />;
    }
    if (configError) {
      return <Alert type="error" showIcon message={configError} />;
    }
    if (!config) return null;
    if (!config.hasTrace || !config.trace) {
      return renderNoTrace();
    }
    return (
      <>
        <div className="mb-6">
          <CockpitControlCard
            chain={config.chain}
            traceConfig={config.trace}
            docNo={docNo}
            traceLoading={traceLoading}
            autoRefresh={autoRefresh}
            lastRefreshedAt={lastRefreshedAt}
            hasTraceResult={!!trace}
            onDocNoChange={setDocNo}
            onTrace={runTrace}
            onAutoRefreshChange={setAutoRefresh}
          />
        </div>
        {trace ? renderTraceResult() : renderGuide()}
      </>
    );
  };

  return (
    <div className="flex gap-4 items-start">
      {/* 左欄：chain 列表（固定 280px） */}
      <ChainListSidebar
        chains={chains}
        loading={chainsLoading}
        selectedChainId={selectedChainId}
        onSelect={selectChain}
      />

      {/* 右欄：駕駛艙（自適應） */}
      <div className="flex-1 min-w-0">{renderCockpit()}</div>
    </div>
  );
};

export default ChainCockpitPanel;
